// ---------------------------------------------------------------------------
// Fonte única dos metadados de cada rota.
//
// Consumida por dois lados:
//   • src/components/Seo.js  — atualiza o <head> na navegação pelo site;
//   • scripts/prerender.js   — escreve o <head> no HTML estático do build.
//
// Posicionamento: atuação nacional, com base em Brasília. As páginas de
// serviço são nacionais; só /advogado-criminalista-brasilia tem recorte local.
// ---------------------------------------------------------------------------

import { site, praca } from './config';
import servicos from './data/servicos';
import brasilia from './data/brasilia';
import faq from './data/faq';

export const URL_BASE = site.url.replace(/\/+$/, '');
const IMAGEM_PADRAO = `${URL_BASE}/imagens/compartilhamento.jpg`;

const ID_PESSOA = `${URL_BASE}/#felipe`;
const ID_SERVICO = `${URL_BASE}/#advocacia`;
const ID_SITE = `${URL_BASE}/#site`;

export const SLUG_BRASILIA = 'advogado-criminalista-brasilia';

/** Todas as rotas que viram HTML estático e entram no sitemap. */
export function todasAsRotas() {
  return [
    '/',
    ...servicos.map((servico) => `/${servico.slug}`),
    `/${SLUG_BRASILIA}`,
    '/sobre',
    '/contato',
  ];
}

/* -------------------------------------------------------------------------
   Entidades reaproveitadas entre as páginas
   ------------------------------------------------------------------------- */

const AREA_NACIONAL = [
  { '@type': 'Country', name: 'Brasil' },
  { '@type': 'City', name: site.endereco.cidade },
  { '@type': 'AdministrativeArea', name: 'Distrito Federal' },
];

/** A pessoa. É esta entidade que sustenta o E-E-A-T do domínio. */
function pessoa() {
  return {
    '@type': 'Person',
    '@id': ID_PESSOA,
    name: site.nome,
    alternateName: site.nomeCurto,
    jobTitle: site.cargo,
    identifier: site.oab,
    description: `${site.cargo} inscrito na ${site.oab}, com atuação dedicada a Direito Penal e Processo Penal em todo o Brasil.`,
    url: `${URL_BASE}/sobre`,
    image: IMAGEM_PADRAO,
    telephone: site.telefoneLink,
    email: site.email,
    knowsAbout: servicos.map((servico) => servico.titulo),
    knowsLanguage: ['pt-BR'],
    worksFor: { '@id': ID_SERVICO },
    sameAs: [site.instagram, site.linkedin].filter(Boolean),
  };
}

/**
 * O serviço jurídico.
 *
 * Sem `address` de propósito: não existe escritório aberto ao público, e
 * declarar um endereço comercial inexistente reprova na verificação do Google
 * e compromete o domínio. A geografia é declarada por `areaServed`, que é o
 * campo correto para quem atende por área e não por ponto físico.
 */
function advocacia() {
  return {
    '@type': 'LegalService',
    '@id': ID_SERVICO,
    name: `${site.nome} — Advocacia Criminal`,
    description: `Advocacia criminal com atuação em todo o Brasil e base em ${praca}.`,
    url: `${URL_BASE}/`,
    telephone: site.telefoneLink,
    email: site.email,
    image: IMAGEM_PADRAO,
    priceRange: '$$',
    knowsLanguage: ['pt-BR'],
    areaServed: AREA_NACIONAL,
    founder: { '@id': ID_PESSOA },
    provider: { '@id': ID_PESSOA },
    availableChannel: [
      {
        '@type': 'ServiceChannel',
        name: 'Atendimento por videoconferência',
        availableLanguage: 'pt-BR',
        serviceUrl: `${URL_BASE}/contato`,
      },
      {
        '@type': 'ServiceChannel',
        name: 'Atendimento presencial no Distrito Federal, com hora marcada',
        availableLanguage: 'pt-BR',
        servicePhone: site.telefoneLink,
      },
    ],
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59',
        description: 'Plantão para prisões em flagrante e casos urgentes',
      },
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Áreas de atuação',
      itemListElement: servicos.map((servico) => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: servico.h1,
          description: servico.resumo,
          url: `${URL_BASE}/${servico.slug}`,
        },
      })),
    },
  };
}

function perguntasFrequentes(lista) {
  return {
    '@type': 'FAQPage',
    mainEntity: lista.map((item) => ({
      '@type': 'Question',
      name: item.pergunta,
      acceptedAnswer: { '@type': 'Answer', text: item.resposta },
    })),
  };
}

function trilha(itens) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: itens.map((item, indice) => ({
      '@type': 'ListItem',
      position: indice + 1,
      name: item.nome,
      item: item.url,
    })),
  };
}

function comContexto(blocos) {
  return { '@context': 'https://schema.org', '@graph': blocos };
}

/* -------------------------------------------------------------------------
   Metadados por rota
   ------------------------------------------------------------------------- */

function metadadosDaHome() {
  return {
    titulo: `Advogado Criminalista | ${site.nome}`,
    descricao: `Advocacia criminal com atuação em todo o Brasil e base em ${praca}, onde ficam o STJ e o STF. Prisão em flagrante, júri, habeas corpus e execução penal.`,
    caminho: '/',
    imagem: IMAGEM_PADRAO,
    jsonLd: comContexto([
      advocacia(),
      pessoa(),
      {
        '@type': 'WebSite',
        '@id': ID_SITE,
        url: `${URL_BASE}/`,
        name: `${site.nome} — Advocacia Criminal`,
        inLanguage: 'pt-BR',
        publisher: { '@id': ID_SERVICO },
      },
      perguntasFrequentes(faq),
    ]),
  };
}

function metadadosDoServico(servico) {
  return {
    titulo: servico.seo.titulo,
    descricao: servico.seo.descricao,
    caminho: `/${servico.slug}`,
    imagem: IMAGEM_PADRAO,
    jsonLd: comContexto([
      {
        '@type': 'Service',
        '@id': `${URL_BASE}/${servico.slug}#servico`,
        name: servico.h1,
        serviceType: servico.titulo,
        description: servico.seo.descricao,
        url: `${URL_BASE}/${servico.slug}`,
        provider: { '@id': ID_PESSOA },
        areaServed: AREA_NACIONAL,
      },
      advocacia(),
      pessoa(),
      trilha([
        { nome: 'Início', url: `${URL_BASE}/` },
        { nome: servico.titulo, url: `${URL_BASE}/${servico.slug}` },
      ]),
      perguntasFrequentes(servico.faq),
    ]),
  };
}

function metadadosDeBrasilia() {
  return {
    titulo: `Advogado Criminalista em ${site.endereco.cidade} | Atuação no ${site.endereco.uf}`,
    descricao: `Defesa criminal em ${praca}: prisão em flagrante, audiência de custódia, Tribunal do Júri e execução penal no TJDFT. Plantão 24 horas para casos urgentes.`,
    caminho: `/${SLUG_BRASILIA}`,
    imagem: IMAGEM_PADRAO,
    jsonLd: comContexto([
      {
        '@type': 'Service',
        '@id': `${URL_BASE}/${SLUG_BRASILIA}#servico`,
        name: brasilia.h1,
        serviceType: 'Advocacia Criminal',
        description: `Atuação criminal no Distrito Federal, perante o TJDFT e as delegacias das regiões administrativas.`,
        url: `${URL_BASE}/${SLUG_BRASILIA}`,
        provider: { '@id': ID_PESSOA },
        areaServed: [
          { '@type': 'City', name: site.endereco.cidade },
          { '@type': 'AdministrativeArea', name: 'Distrito Federal' },
        ],
      },
      advocacia(),
      pessoa(),
      trilha([
        { nome: 'Início', url: `${URL_BASE}/` },
        { nome: `Advogado Criminalista em ${site.endereco.cidade}`, url: `${URL_BASE}/${SLUG_BRASILIA}` },
      ]),
      perguntasFrequentes(brasilia.faq),
    ]),
  };
}

function metadadosDoSobre() {
  return {
    titulo: `${site.nome} | ${site.cargo} — ${site.oab}`,
    descricao: `Quem é ${site.nome}, advogado criminalista inscrito na ${site.oab}, como conduz a defesa e em que fases da persecução penal a atuação começa.`,
    caminho: '/sobre',
    imagem: IMAGEM_PADRAO,
    jsonLd: comContexto([
      {
        '@type': 'ProfilePage',
        '@id': `${URL_BASE}/sobre#pagina`,
        url: `${URL_BASE}/sobre`,
        name: `${site.nome} — ${site.cargo}`,
        inLanguage: 'pt-BR',
        mainEntity: { '@id': ID_PESSOA },
      },
      pessoa(),
      advocacia(),
      trilha([
        { nome: 'Início', url: `${URL_BASE}/` },
        { nome: 'O Advogado', url: `${URL_BASE}/sobre` },
      ]),
    ]),
  };
}

function metadadosDoContato() {
  return {
    titulo: `Contato | ${site.cargo} ${site.nomeCurto}`,
    descricao: `Fale com o advogado por WhatsApp, telefone ou e-mail. Atendimento presencial no Distrito Federal com hora marcada e por videoconferência em todo o Brasil.`,
    caminho: '/contato',
    imagem: IMAGEM_PADRAO,
    jsonLd: comContexto([
      {
        '@type': 'ContactPage',
        '@id': `${URL_BASE}/contato#pagina`,
        url: `${URL_BASE}/contato`,
        name: 'Contato',
        inLanguage: 'pt-BR',
        mainEntity: { '@id': ID_SERVICO },
      },
      advocacia(),
      pessoa(),
      trilha([
        { nome: 'Início', url: `${URL_BASE}/` },
        { nome: 'Contato', url: `${URL_BASE}/contato` },
      ]),
    ]),
  };
}

const NAO_ENCONTRADA = {
  titulo: `Página não encontrada | ${site.nome}`,
  descricao:
    'A página que você procurava não existe ou foi movida. Veja as áreas de atuação e os canais de contato do escritório.',
  caminho: '/404',
  imagem: IMAGEM_PADRAO,
  semIndexacao: true,
  jsonLd: null,
};

/**
 * Metadados completos de uma rota.
 * @param {string} caminho ex.: "/" ou "/habeas-corpus"
 */
export function metadados(caminho) {
  const limpo = `/${String(caminho || '/').replace(/^\/+|\/+$/g, '')}`;

  if (limpo === '/') return metadadosDaHome();
  if (limpo === `/${SLUG_BRASILIA}`) return metadadosDeBrasilia();
  if (limpo === '/sobre') return metadadosDoSobre();
  if (limpo === '/contato') return metadadosDoContato();

  const servico = servicos.find((item) => `/${item.slug}` === limpo);
  if (servico) return metadadosDoServico(servico);

  return NAO_ENCONTRADA;
}

/** URL absoluta canônica de uma rota. */
export function canonical(caminho) {
  return caminho === '/' ? `${URL_BASE}/` : `${URL_BASE}${caminho}`;
}
